// src/components/staff/StaffRolesManager.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';
import { useRoles } from '../../roles/hooks/useRoles';
import { useStaff } from '../hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { useStaffPermissions } from '../../auth/hooks/usePermissions';
import StaffTabSection from '../pages/StaffDetailsPage/components/StaffTabSection';

export default function StaffRolesManager({ staffMember, staffRoles, onUpdate }) {
  const { canRemoveRoles } = useStaffPermissions();
  const { t } = useTranslation(['staff', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedRoleToAssign, setSelectedRoleToAssign] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const { 
    roles,
    fetchDepartmentRoles,
  } = useRoles();

  const { 
    assignRole, 
    removeRole, 
  } = useStaff();

  // Load data when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load all department roles
        const rolesResult = await fetchDepartmentRoles();
        if (rolesResult.success) {
          setAllRoles(roles);
        }
      } catch (error) {
        console.error('Error loading roles data:', error);
        showError(t('staff:errors.load_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchDepartmentRoles, showError, t]);

  // Update allRoles when roles change
  useEffect(() => {
    setAllRoles(roles);
  }, [roles]);

  const handleAssignRole = useCallback(async () => {
    if (!selectedRoleToAssign || !staffMember) return;

    setAssigning(true);
    try {
      const result = await assignRole(staffMember.staff_member_id, selectedRoleToAssign.role_id);
      if (result.success) {
        showSuccess(t('roles:success.role_assigned', { 
          roleName: selectedRoleToAssign.name, 
          staffName: staffMember.name 
        }));
        setAssignDialogOpen(false);
        setSelectedRoleToAssign(null);
        onUpdate?.();
      } else {
        showError(result.error || t('roles:errors.assign_failed'));
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      showError(t('roles:errors.assign_failed'));
    } finally {
      setAssigning(false);
    }
  }, [selectedRoleToAssign, staffMember, assignRole, showSuccess, showError, onUpdate, t]);

  const handleRemoveRole = useCallback((role) => {
    showConfirmDialog({
      title: t('staff:confirm.remove_role_title'),
      message: t('staff:confirm.remove_role_message', { 
        roleName: role.name, 
        staffName: staffMember.name 
      }),
      confirmText: t('staff:confirm.remove_confirm'),
      cancelText: t('common:buttons.cancel'),
      severity: 'warning',
      onConfirm: async () => {
        try {
          const result = await removeRole(staffMember.staff_member_id, role.role_id);
          
          if (result.success) {
            showSuccess(t('roles:success.role_removed', { 
              roleName: role.name, 
              staffName: staffMember.name 
            }));
            onUpdate?.();
          } else {
            console.error('Remove role failed:', result.error);
            showError(result.error || t('roles:errors.remove_failed'));
          }
        } catch (error) {
          console.error('Error removing role:', error);
          showError(t('roles:errors.remove_failed') + ': ' + (error.message || t('staff:errors.unexpected_error')));
        }
      },
    });
  }, [staffMember, removeRole, showConfirmDialog, showSuccess, showError, onUpdate, t]);

  // Get roles that the staff member doesn't already have
  const availableRoles = allRoles.filter(role => 
    !staffRoles.some(assigned => assigned.role_id === role.role_id)
  );

  if (!staffMember) {
    return (
      <Alert severity="info">
        {t('staff:messages.select_staff_for_details')}
      </Alert>
    );
  }

  return (
    <>
      <StaffTabSection
        icon={SecurityIcon}
        title={t('staff:roles_manager.title', { name: staffMember.name })}
        subtitle={t('staff:roles_manager.assigned_count', { count: staffRoles.length })}
        loading={loading}
        fill
        actions={(
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAssignDialogOpen(true)}
            disabled={availableRoles.length === 0}
          >
            {t('staff:roles_manager.assign_role')}
          </Button>
        )}
      >
        {staffRoles.length === 0 ? (
          <Alert severity="info">
            {t('staff:roles_manager.no_roles_assigned')}
            {availableRoles.length > 0 && (
              <Box mt={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAssignDialogOpen(true)}
                >
                  {t('staff:roles_manager.assign_first_role')}
                </Button>
              </Box>
            )}
          </Alert>
        ) : (
          <List>
            {staffRoles.map((role) => (
              <ListItem key={role.role_id} divider>
                <ListItemIcon>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                    <VpnKeyIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight="medium">
                        {role.name}
                      </Typography>
                      <Chip
                        label={t('staff:roles_manager.permissions_count', { count: role.permissions_count || 0 })}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {role.description || t('staff:roles_manager.no_description')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('staff:roles_manager.role_id_short')}: {role.role_id.substring(0, 8)}...
                      </Typography>
                      {role.assigned_at && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('staff:roles_manager.assigned_time', { 
                            time: <LocalizedDateDistance date={role.assigned_at} />
                          })}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                {canRemoveRoles && (
                  <ListItemSecondaryAction>
                    <Tooltip title={t('staff:roles_manager.remove_role_tooltip')}>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveRole(role)}
                        color="error"
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        )}

        {availableRoles.length === 0 && staffRoles.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('staff:roles_manager.all_roles_assigned')}
          </Alert>
        )}

        {allRoles.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('staff:roles_manager.no_roles_in_department')}
          </Alert>
        )}
      </StaffTabSection>

      {/* Assign Role Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('staff:roles_manager.assign_role_dialog_title', { name: staffMember.name })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('staff:roles_manager.assign_role_dialog_description', { name: staffMember.name })}
          </Typography>

          <Autocomplete
            options={availableRoles}
            getOptionLabel={(option) => `${option.name} - ${option.description || t('staff:roles_manager.no_description')}`}
            value={selectedRoleToAssign}
            onChange={(event, value) => setSelectedRoleToAssign(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('staff:roles_manager.select_role')}
                placeholder={t('staff:roles_manager.search_for_role')}
                margin="normal"
                fullWidth
              />
            )}
            renderOption={({ key, ...props }, option) => (
              <Box key={key} component="li" {...props}>
                <Avatar sx={{ mr: 2, width: 32, height: 32, bgcolor: 'primary.main' }}>
                  <VpnKeyIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="body2">
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description || t('staff:roles_manager.no_description')} • {t('staff:roles_manager.permissions_count', { count: option.permissions_count || 0 })}
                  </Typography>
                </Box>
              </Box>
            )}
            noOptionsText={
              availableRoles.length === 0 
                ? t('staff:roles_manager.all_roles_already_assigned')
                : t('staff:roles_manager.no_matching_roles')
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={handleAssignRole}
            variant="contained"
            disabled={!selectedRoleToAssign || assigning}
            startIcon={assigning ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {assigning ? t('staff:roles_manager.assigning') : t('staff:roles_manager.assign_role')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </>
  );
}